/**
 * ELK Layout Engine Wrapper
 * Orchestrates the BPMN layout pipeline using specialized handlers.
 *
 * Lightweight post-processing: Only repositions boundary event targets below main flow.
 * ELK handles all edge routing.
 */

import ELK from 'elkjs';
import type { ElkNode } from 'elkjs';
import type { ElkBpmnGraph, ElkLayoutOptions } from '../types';
import type { LayoutedGraph } from '../types/elk-output';
import { SizeCalculator } from './size-calculator';
import { BoundaryEventHandler } from './post-processing/boundary-event';
import { ArtifactPositioner } from './post-processing/artifact-positioner';
import { GroupPositioner } from './post-processing/group-positioner';
import { LaneArranger } from './post-processing/lane-arranger';
import { PoolArranger } from './post-processing/pool-arranger';
import { Compactor } from './post-processing/compactor';
import { EdgeFixer } from './edge-routing';
import { ElkGraphPreparer } from './preparation/elk-graph-preparer';
import { ResultMerger } from './preparation/result-merger';
import { isDebugEnabled } from '../utils/debug';

export interface ElkLayouterOptions {
  elkOptions?: ElkLayoutOptions;
  /** Enable layout compaction to reduce whitespace (default: true) */
  enableCompaction?: boolean;
}

export class ElkLayouter {
  private elk: InstanceType<typeof ELK>;
  private userOptions: ElkLayoutOptions;
  private enableCompaction: boolean;
  private sizeCalculator: SizeCalculator;
  private boundaryEventHandler: BoundaryEventHandler;
  private artifactPositioner: ArtifactPositioner;
  private groupPositioner: GroupPositioner;
  private laneArranger: LaneArranger;
  private poolArranger: PoolArranger;
  private compactor: Compactor;
  private edgeFixer: EdgeFixer;
  private graphPreparer: ElkGraphPreparer;
  private resultMerger: ResultMerger;

  constructor(options?: ElkLayouterOptions) {
    this.elk = new ELK();
    this.userOptions = options?.elkOptions ?? {};
    this.enableCompaction = options?.enableCompaction ?? true;
    this.sizeCalculator = new SizeCalculator();
    this.boundaryEventHandler = new BoundaryEventHandler();
    this.artifactPositioner = new ArtifactPositioner();
    this.groupPositioner = new GroupPositioner();
    this.laneArranger = new LaneArranger();
    this.poolArranger = new PoolArranger();
    this.compactor = new Compactor();
    this.edgeFixer = new EdgeFixer();
    this.graphPreparer = new ElkGraphPreparer();
    this.resultMerger = new ResultMerger();
  }

  /**
   * Run ELK layout on the graph
   */
  async layout(graph: ElkBpmnGraph): Promise<LayoutedGraph> {
    // Deep clone to avoid mutating the original
    const graphCopy = JSON.parse(JSON.stringify(graph)) as ElkBpmnGraph;

    // Apply default sizes to all nodes
    const sizedGraph = this.sizeCalculator.applyDefaultSizes(graphCopy);

    // Collect boundary event info - used to set ELK constraints for target nodes
    const boundaryEventInfo = this.boundaryEventHandler.collectInfo(sizedGraph);

    // Collect boundary event target IDs for ELK constraint assignment
    const boundaryEventTargetIds = this.graphPreparer.collectBoundaryEventTargetIds(boundaryEventInfo);

    // Collect artifact association info for post-processing
    const artifactInfo = this.artifactPositioner.collectInfo(sizedGraph);

    // Collect Group info for post-processing (Groups will be repositioned after layout)
    const groupInfo = this.groupPositioner.collectInfo(sizedGraph);

    // Prepare graph for ELK (convert to ELK format)
    // Pass boundaryEventTargetIds so ELK can apply position constraints
    const elkGraph = this.graphPreparer.prepare(sizedGraph, this.userOptions, boundaryEventTargetIds);

    // Run ELK layout
    const layoutedElkGraph = await this.elk.layout(elkGraph);

    // Lightweight post-processing: Move boundary event targets below their attached tasks
    // This is BPMN-specific positioning that ELK cannot handle via constraints
    const movedNodes = this.boundaryEventHandler.identifyNodesToMove(
      layoutedElkGraph, boundaryEventInfo, sizedGraph, isDebugEnabled()
    );

    if (movedNodes.size > 0) {
      // Move nodes
      this.boundaryEventHandler.applyNodeMoves(layoutedElkGraph, movedNodes);
      // Recalculate edges for moved nodes
      this.boundaryEventHandler.recalculateEdgesForMovedNodes(layoutedElkGraph, movedNodes, boundaryEventInfo);
    }

    // Reposition artifacts (data objects, data stores, annotations) to be near their associated tasks
    this.artifactPositioner.reposition(layoutedElkGraph, artifactInfo);

    // Rearrange lanes to stack vertically within pools (ELK's partitioning doesn't do this correctly)
    this.laneArranger.rearrange(layoutedElkGraph, sizedGraph);

    // Rearrange pools to stack vertically within collaborations
    this.poolArranger.rearrange(layoutedElkGraph, sizedGraph);

    // Reposition Groups to surround their grouped elements
    this.groupPositioner.reposition(layoutedElkGraph, groupInfo, sizedGraph);

    // Recalculate artifact edges with obstacle avoidance
    this.artifactPositioner.recalculateWithObstacleAvoidance(layoutedElkGraph, artifactInfo);

    // Apply layout compaction to reduce whitespace (if enabled)
    if (this.enableCompaction) {
      this.compactor.compact(layoutedElkGraph);
    }

    // Pull content up to the top so canvas doesn't lead with empty space
    // (ELK sometimes reserves vertical bands that post-processing leaves empty)
    this.normalizeOrigin(layoutedElkGraph);

    // Update container bounds to include all moved children
    this.updateContainerBounds(layoutedElkGraph);

    // Final edge correction pass — must run AFTER updateContainerBounds so
    // edge endpoints snap to the post-shrink container borders. Otherwise
    // edges anchored to a wider pre-shrink subprocess float past its new edge.
    this.edgeFixer.fix(layoutedElkGraph);

    // Merge layout results back with BPMN metadata
    return this.resultMerger.merge(sizedGraph, layoutedElkGraph);
  }

  // Shift each top-level container so its leftmost/topmost node sits near
  // the origin: ELK reserves bands for nodes that post-processing later
  // relocates (e.g. boundary-event targets), and we want the canvas to lead
  // with content, not empty space.
  private normalizeOrigin(graph: ElkNode): void {
    if (!graph.children) return;
    const margin = 12;

    for (const top of graph.children) {
      // Find min x/y across direct children of this top-level container.
      // We only care about positions inside `top`, since `top` itself sits
      // at (0,0) in the root canvas after ELK.
      if (!top.children || top.children.length === 0) continue;

      // Boundary events are repositioned onto their parent task by the
      // diagram builder, so their ELK-assigned y is phantom and must be
      // ignored when finding the topmost visible content.
      const isPhantom = (child: ElkNode): boolean => {
        const bpmn = (child as ElkNode & { bpmn?: { type?: string } }).bpmn;
        return bpmn?.type === 'boundaryEvent';
      };

      let minX = Infinity;
      let minY = Infinity;
      for (const child of top.children) {
        if (isPhantom(child)) continue;
        const x = child.x ?? 0;
        const y = child.y ?? 0;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
      }
      if (!isFinite(minX) || !isFinite(minY)) continue;

      const shiftX = margin - minX;
      const shiftY = margin - minY;
      if (shiftX === 0 && shiftY === 0) continue;

      for (const child of top.children) {
        child.x = (child.x ?? 0) + shiftX;
        child.y = (child.y ?? 0) + shiftY;
      }
      // Shift edge waypoints attached at this container level too.
      if (top.edges) {
        for (const edge of top.edges) {
          if (!edge.sections) continue;
          for (const section of edge.sections) {
            section.startPoint.x += shiftX;
            section.startPoint.y += shiftY;
            section.endPoint.x += shiftX;
            section.endPoint.y += shiftY;
            if (section.bendPoints) {
              for (const bp of section.bendPoints) {
                bp.x += shiftX;
                bp.y += shiftY;
              }
            }
          }
        }
      }
    }
  }

  private updateContainerBounds(graph: ElkNode): void {
    type Bpmned = ElkNode & { bpmn?: { type?: string } };

    const isLane = (n: Bpmned): boolean => n.bpmn?.type === 'lane';
    const hasLaneChildren = (n: ElkNode): boolean =>
      !!n.children?.some(c => isLane(c as Bpmned));

    const fit = (node: ElkNode): void => {
      if (!node.children || node.children.length === 0) return;

      for (const child of node.children) fit(child);

      // Authoritative sizing: don't touch lanes or pools-with-lanes.
      if (isLane(node as Bpmned)) return;
      if (hasLaneChildren(node)) return;

      const padding = 12;
      let requiredWidth = 0;
      let requiredHeight = 0;
      for (const child of node.children) {
        requiredWidth = Math.max(requiredWidth, (child.x ?? 0) + (child.width ?? 0) + padding);
        requiredHeight = Math.max(requiredHeight, (child.y ?? 0) + (child.height ?? 0) + padding);
      }
      node.width = requiredWidth;
      node.height = requiredHeight;
    };

    fit(graph);
  }
}
